import React, { useState, useCallback, useRef, useMemo, useEffect, useContext, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Menu, MenuItem } from "@mui/material";
import { useFormik } from "formik";

import COMMON from "common";
import api from "services/api";
import pathnames from "routes/pathnames";
import sanitizeObject from "common/sanitize-object";
import serveRequestErrors from "common/serve-request-errors";
import queryParamsEntries from "common/query-params-entries";
import { promptAlertMessage } from "store/slices/alert";
import { AxiosContext } from "contexts/with-interceptor-provider";
import { groupCalendarsByRegion, formatYearCount, formatHolidayCount } from "common/holiday-calendar";
import getHolidayRegionListing from "services/get-holiday-region-listing";
import AppInput from "components/app-input";
import AppButton from "components/app-button";
import AppEmptyState from "components/app-empty-state";
import AppSearchInput from "components/app-search-input";
import AppSelectInput from "components/app-select-input";
import AppMapPinIcon from "components/icons/app-map-pin-icon";
import AppEllipsisIcon from "components/icons/app-ellipsis-icon";
import AppCalendarDaysIcon from "components/icons/app-calendar-days-icon";
import AppHolidayCalendarModal from "components/pages/leave-management/app-holiday-calendar-modal";
import AppConfirmDeleteHolidayCalendarModal from "components/pages/leave-management/app-confirm-delete-holiday-calendar-modal";
import addIcon from "assets/images/add-icon.svg";
import editIcon from "assets/images/edit-icon.svg";
import removeIcon from "assets/images/remove-icon.svg";
import filterIcon from "assets/images/components/app-multi-select-input/filter-icon.svg";

const PageHolidayCalendars = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const modalRef = useRef();
	const searchInputRef = useRef();
	const confirmDeleteRef = useRef();
	const context = useContext(AxiosContext);
	const [searchParams, setSearchParams] = useSearchParams();
	const memoSetSearchParams = useRef(setSearchParams);
	//prettier-ignore
	const paramsRef = useRef({
		title: searchParams.get("title") || "",
		regionCode: searchParams.get("regionCode") || "",
		year: searchParams.get("year") || "",
	});
	/* null means "not fetched yet" so the empty state does not flash before the first response */
	const [records, setRecords] = useState(null);
	const [regionOptions, setRegionOptions] = useState([]);
	const [anchorEl, setAnchorEl] = useState(null);
	const [menuCalendar, setMenuCalendar] = useState(null);
	const cancelRequest = useMemo(() => context.onHandleCancelRequest, [context.onHandleCancelRequest]);
	const calendars = useMemo(() => groupCalendarsByRegion(records), [records]);
	const hasActiveFilters = useMemo(() => {
		const params = queryParamsEntries(searchParams);

		return !!(params.title || params.regionCode || params.year);
	}, [searchParams]);
	/* Only "no calendars ever created" hides the header and shows the create-focused empty
	   state below - filtering down to zero results keeps the search bar so it can be cleared. */
	const isEmptyState = useMemo(() => !!records && !calendars.length && !hasActiveFilters, [records, calendars, hasActiveFilters]);
	const isEmptySearchResult = useMemo(() => !!records && !calendars.length && hasActiveFilters, [records, calendars, hasActiveFilters]);
	const initialValues = useMemo(() => ({ title: "", regionCode: "", year: "" }), []);
	const formik = useFormik({
		initialValues,
		onSubmit: (values) => {
			onHandleSubmitSearch(values);
		},
	});

	const setValues = useMemo(() => formik.setValues, [formik]);

	const advanceSearchValues = useMemo(() => {
		const params = queryParamsEntries(searchParams);
		const region = regionOptions.find((o) => o.value === params.regionCode);

		return { title: params.title, regionCode: region?.label || params.regionCode, year: params.year };
	}, [searchParams, regionOptions]);

	const onHandleRemoveField = (field) => {
		formik.setFieldValue(field, "");

		paramsRef.current = { ...paramsRef.current, [field]: "" };

		onHandleGetList();
	};

	const onHandleResetSearch = () => {
		formik.setValues(formik.initialValues);

		paramsRef.current = { ...formik.initialValues };

		onHandleGetList();
	};

	const onHandleSubmitSearch = (values) => {
		searchInputRef.current.onhandleCloseAdvanceSearch();

		paramsRef.current = { ...paramsRef.current, ...values };

		onHandleGetList();
	};

	const onHandleShowAdvanceSearch = (event) => {
		if (searchInputRef.current?.onHandleShowAdvanceSearch) {
			searchInputRef.current.onHandleShowAdvanceSearch(event);
		}
	};

	/* /holiday-calendar/search returns a plain array of one-region-one-year records, filtered
	   by whatever criteria are active. They are grouped by regionCode into the calendars
	   rendered below, so every year of a region lives behind a single card. */
	const onHandleGetList = useCallback(async () => {
		let response = null;

		try {
			const payload = sanitizeObject(paramsRef.current);

			memoSetSearchParams.current(payload, { replace: true });

			response = await api.get.holidayCalendar.search(payload);
		} catch (error) {
			serveRequestErrors(error);
		}

		if (response) setRecords(response);
	}, []);

	const onHandleCreate = useCallback(() => {
		modalRef.current.onHandleShow();
	}, []);

	//prettier-ignore
	const onHandleSelectCalendar = useCallback((regionCode) => {
		navigate(pathnames.leaveManagement.holidayCalendar + encodeURIComponent(regionCode));
	}, [navigate]);

	const onHandleOpenCardMenu = useCallback((event, calendar) => {
		event.stopPropagation();
		setAnchorEl(event.currentTarget);
		setMenuCalendar(calendar);
	}, []);

	const onHandleCloseCardMenu = useCallback(() => {
		setAnchorEl(null);
	}, []);

	/* The grid can be filtered down to a single title or year, so a card's `years` may not
	   cover every year of the region. Edit and delete act on the whole calendar, so both
	   refetch by regionCode alone first to make sure they see every year. */
	const onHandleGetFullCalendar = useCallback(async (regionCode) => {
		let response = null;

		try {
			response = await api.get.holidayCalendar.search({ regionCode });
		} catch (error) {
			serveRequestErrors(error);
			return null;
		}

		return groupCalendarsByRegion(response).find((o) => o.regionCode === regionCode) || null;
	}, []);

	const onHandleEditCalendar = useCallback(async () => {
		setAnchorEl(null);

		if (!menuCalendar) return;

		const full = await onHandleGetFullCalendar(menuCalendar.regionCode);

		if (full) modalRef.current.onHandleShow(full);
	}, [menuCalendar, onHandleGetFullCalendar]);

	//prettier-ignore
	const onHandleDeleteCalendar = useCallback(async () => {
		setAnchorEl(null);

		if (!menuCalendar) return;

		const full = await onHandleGetFullCalendar(menuCalendar.regionCode);

		if (!full) return;

		confirmDeleteRef.current.onHandleShow({
			title: "Delete Holiday Calendar",
			description: "This removes " + full.title + " (" + formatYearCount(full.yearCount) + ", " + formatHolidayCount(full.holidayCount) + ") and stops excluding these holidays from leave calculations.",
			confirmLabel: "Delete Calendar",
			onConfirm: async () => {
				try {
					/* One record per year means one delete per year; sequential so a failure
					   part way through does not leave the rest fired off blindly. */
					for (const year of full.years) {
						await api.post.holidayCalendar.delete(year.id);
					}
				} catch (error) {
					serveRequestErrors(error);
					return false;
				}

				dispatch(promptAlertMessage({ message: "Holiday calendar has been deleted successfully" }));
				onHandleGetList();

				return true;
			},
		});
	}, [menuCalendar, onHandleGetFullCalendar, dispatch, onHandleGetList]);

	const headerButtons = useMemo(() => {
		const buttons = [
			{ label: "Filter", icon: filterIcon, outline: true, onClick: onHandleShowAdvanceSearch },
			{ label: "Create Holiday Calendar", icon: addIcon, onClick: onHandleCreate },
		];
		return buttons;
	}, [onHandleCreate]);

	useEffect(() => {
		onHandleGetList();
	}, [onHandleGetList]);

	useEffect(() => {
		getHolidayRegionListing()
			.then((res) => setRegionOptions(res.content))
			.catch((error) => serveRequestErrors(error));
	}, []);

	useEffect(() => {
		setValues((prev) => ({ ...prev, ...paramsRef.current }));
	}, [setValues]);

	useEffect(() => {
		return () => {
			cancelRequest(COMMON.ENDPOINT_PATH.HOLIDAY_CALENDAR.SEARCH);
		};
	}, [cancelRequest]);

	return (
		<div className="page-holiday-calendars">
			<div className="holiday-calendars">
				{isEmptyState && <AppEmptyState title="No holiday calendars yet" description="Create a calendar for a region to exclude its public holidays from leave day calculations." buttons={[{ label: "Create Holiday Calendar", icon: addIcon, onClick: onHandleCreate }]} />}

				{!isEmptyState && (
					<Fragment>
						<div className="holiday-calendars__header">
							<AppSearchInput ref={searchInputRef} multiValues={advanceSearchValues} onRemoveField={onHandleRemoveField} buttons={headerButtons}>
								<form className="app-advance-search-form" onSubmit={formik.handleSubmit}>
									<div className="advance-form">
										<div className="advance-form__inputs">
											<AppInput type="text" name="title" label="Calendar Name" placeholder="Enter calendar name" value={formik.values.title} onChange={formik.handleChange} />

											<AppSelectInput searchable={false} name="regionCode" label="Region" placeholder="Select a region" options={regionOptions} value={formik.values.regionCode} onChange={formik.handleChange} />

											<AppInput type="number" maxLength={4} name="year" label="Year" placeholder="e.g. 2026" value={formik.values.year} onChange={formik.handleChange} />
										</div>

										<div className="advance-form__button-container">
											<AppButton type="button" label="Clear" outline onClick={onHandleResetSearch} />
											<AppButton type="submit" label="Search" />
										</div>
									</div>
								</form>
							</AppSearchInput>
						</div>

						{isEmptySearchResult && <AppEmptyState title="No holiday calendars found" description="Try adjusting your search or filters." />}
					</Fragment>
				)}

				{!!calendars.length && (
					<div className="holiday-calendars__grid">
						{calendars.map((calendar) => (
							// prettier-ignore
							<div className="holiday-calendars__card" key={calendar.regionCode} role="button" tabIndex={0} onClick={() => onHandleSelectCalendar(calendar.regionCode)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); onHandleSelectCalendar(calendar.regionCode); } }}>
								<div className="holiday-calendars__card-header">
									<div className="holiday-calendars__card-title-row">
										<p className="holiday-calendars__card-title">{calendar.title}</p>

										<button type="button" className="holiday-calendars__card-menu-trigger" aria-label="calendar actions" onClick={(event) => onHandleOpenCardMenu(event, calendar)} onKeyDown={(event) => event.stopPropagation()}>
											<AppEllipsisIcon />
										</button>
									</div>

									<div className="holiday-calendars__card-region">
										<AppMapPinIcon className="holiday-calendars__card-pin" />
										<p className="holiday-calendars__card-region-text">{calendar.region || calendar.regionCode}</p>
									</div>
								</div>

								<div className="holiday-calendars__card-footer">
									<div className="holiday-calendars__card-meta">
										<AppCalendarDaysIcon className="holiday-calendars__card-meta-icon" />
										<p className="holiday-calendars__card-meta-text">{formatYearCount(calendar.yearCount)}</p>
									</div>
								</div>
							</div>
						))}
					</div>
				)}

				<Menu classes={{ root: "app-table-menu" }} anchorEl={anchorEl} open={!!anchorEl} onClose={onHandleCloseCardMenu} anchorOrigin={{ vertical: "bottom", horizontal: "right" }} transformOrigin={{ vertical: "top", horizontal: "right" }}>
					<MenuItem onClick={onHandleEditCalendar}>
						<img className="app-table-menu__icon" src={editIcon} alt="edit" />
						Edit
					</MenuItem>

					<MenuItem onClick={onHandleDeleteCalendar}>
						<img className="app-table-menu__icon" src={removeIcon} alt="delete" />
						Delete
					</MenuItem>
				</Menu>
			</div>

			<AppHolidayCalendarModal ref={modalRef} records={records} onHandleGetList={onHandleGetList} />

			<AppConfirmDeleteHolidayCalendarModal ref={confirmDeleteRef} />
		</div>
	);
};

export default PageHolidayCalendars;
